<template>
  <LoadingSpinner v-if="loadingWorkOrder || projectLoading || subsLoading" />

  <div v-else class="edit-work-order">
    <PageHeader title="Edit Work Order" />

    <div class="mx-auto max-w-7xl">
      <WorkOrderUpdateForm
        :model="workOrder"
        :project="project"
        :subcontractors="subs"
        :save-function="updateWorkOrder"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { WorkOrder } from '@/models/workOrder'
import WorkOrderUpdateForm from '../components/WorkOrderUpdateForm.vue'
import PageHeader from '@/components/app/AppPageHeader.vue'
import { useHead } from '@unhead/vue'
import PorjectService from '@/services/ProjectService'
import { useRouter, useRoute } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import SubcontractorService from '@/services/SubcontractorService'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import WorkOrderService from '@/services/WorkOrderService'
import BudgetService from '@/services/BudgetService'
import { computed } from 'vue'

useHead({ title: 'Edit Work Order' })

const $router = useRouter()
const $route = useRoute()
const { notify } = useNotification()
const queryClient = useQueryClient()
const subcontractorService = new SubcontractorService()
const projectService = new PorjectService()
const budgetService = new BudgetService()
const workOrderId = $route.params.id as string || ''
const workOrderService = new WorkOrderService('')

const { data: workOrder, isLoading:loadingWorkOrder } = useQuery({
  queryKey: ['workOrder', workOrderId],
  queryFn: () => workOrderService.find(workOrderId)
})

const enbleProjectQuery = computed(() => workOrder.value?.projectId !== '')

const { data:project, isLoading:projectLoading } = useQuery({
  queryKey: ['project', workOrder.value?.projectId],
  queryFn: () => projectService.find(workOrder.value?.projectId),
  enabled: enbleProjectQuery
})

const { data:subs, isLoading:subsLoading } = useQuery({
  queryKey: ['subcontractors'],
  queryFn: () => subcontractorService.all().then((subcontractors) => subcontractors.map((subcontractor) => ({ value: subcontractor.id, label: subcontractor.name })))
})

const { mutateAsync:updateWorkOrder } = useMutation({
  mutationFn: (workOrder: WorkOrder) => budgetService.updateWorkOrder(workOrder.budgetId, workOrder.id, workOrder),
  onSuccess: (updatedWorkOrder: WorkOrder) => {
    queryClient.setQueryData(['workOrder', updatedWorkOrder.id], updatedWorkOrder)
    queryClient.invalidateQueries({ queryKey: ['workOrders'] })
    notify({
      type: 'success',
      title: 'Work Order updated',
      text: 'Work Order updated successfully'
    })
    $router.push({ name: 'ShowWorkOrder', params: { id: updatedWorkOrder.id }, hash: '#showConfirmation' })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error',
      text: error.message || 'An error occurred while trying to update the work order'
    })
  }
})

</script>
